import { useState, useCallback } from 'react';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterGroup {
  label: string;
  options: FilterOption[];
  nested?: boolean;
  nestedGroups?: Record<string, FilterOption[]>;
}

export interface FilterConfig {
  [key: string]: FilterGroup;
}

export interface FilterState {
  [key: string]: string[];
}

export interface UseGenericFilterProps {
  config: FilterConfig;
  onFilterChange?: (filters: FilterState) => void;
}

export const useGenericFilter = ({ 
  config, 
  onFilterChange 
}: UseGenericFilterProps) => {
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [filterValues, setFilterValues] = useState<FilterState>({});

  const addActiveFilter = useCallback((filterKey: string) => {
    setActiveFilters(prev => {
      if (!prev.includes(filterKey)) {
        return [...prev, filterKey];
      }
      return prev;
    });
  }, []);

  const removeActiveFilter = useCallback((filterKey: string) => {
    setActiveFilters(prev => prev.filter(f => f !== filterKey));
    setFilterValues(prev => {
      const newValues = { ...prev };
      delete newValues[filterKey];
      onFilterChange?.(newValues);
      return newValues;
    });
  }, [onFilterChange]);

  const updateFilterValues = useCallback((filterKey: string, values: string[]) => {
    setFilterValues(prev => {
      const newValues = { ...prev, [filterKey]: values };
      onFilterChange?.(newValues);
      return newValues;
    });
  }, [onFilterChange]);

  const toggleFilterValue = useCallback((filterKey: string, value: string) => {
    setFilterValues(prev => {
      const currentValues = prev[filterKey] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      const updatedState = { ...prev, [filterKey]: newValues };
      onFilterChange?.(updatedState);
      return updatedState;
    });
  }, [onFilterChange]);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setFilterValues({});
    onFilterChange?.({});
  }, [onFilterChange]);

  const clearFilter = useCallback((filterKey: string) => {
    removeActiveFilter(filterKey);
  }, [removeActiveFilter]);

  const hasActiveFilters = activeFilters.length > 0;
  const getActiveFilterCount = (filterKey: string) => filterValues[filterKey]?.length || 0;

  return {
    activeFilters,
    filterValues,
    addActiveFilter,
    removeActiveFilter,
    updateFilterValues,
    toggleFilterValue,
    clearAllFilters,
    clearFilter,
    hasActiveFilters,
    getActiveFilterCount,
    config
  };
};